import { dayjs } from "./dayjs.min.js";

// 姓名智能脱敏
export const smartDesensitize = (name) => {
  if (!name) return "";
  return name.length <= 2 ? name.substring(0, 1) + "**" : name[0] + "*" + name[name.length - 1];
};

/**
 * 时间日期转换
 * @param date 当前时间，new Date() 格式
 * @param format 需要转换的时间格式字符串
 * @description format 字符串随意，如 `YYYY-MM、YYYY-MM-DD`
 * @description format 季度："YYYY-MM-DD HH:mm:ss QQQQ"
 * @description format 星期："YYYY-MM-DD HH:mm:ss WWW"
 * @description format 几周："YYYY-MM-DD HH:mm:ss ZZZ"
 * @description format 季度 + 星期 + 几周："YYYY-MM-DD HH:mm:ss WWW QQQQ ZZZ"
 * @returns 返回拼接后的时间字符串
 */
export const formatDate = (date, format = null) => {
  // 日期不存在，则返回空
  if (!date) {
    return "";
  }
  // 日期存在，则进行格式化
  return date ? dayjs(date).format(format ?? "YYYY-MM-DD HH:mm:ss") : "";
};

/**
 * 设置起始日期，时间为00:00:00
 * @param param 传入日期
 * @returns 带时间00:00:00的日期
 */
export const beginOfDay = (param) => {
  if (param.indexOf("年") != -1) {
    param = param.replace("年", "-").replace("月", "");
  }
  return dayjs(param).startOf("month").format("YYYY-MM-DD HH:mm:ss");
};

/**
 * 设置结束日期，时间为23:59:59
 * @param param 传入日期
 * @returns 带时间23:59:59的日期
 */
export const endOfDay = (param) => {
  if (param.indexOf("年") != -1) {
    param = param.replace("年", "-").replace("月", "");
  }
  return dayjs(param).endOf("month").format("YYYY-MM-DD HH:mm:ss");
};

/**
 * 获取指定日期的开始时间、截止时间
 * @param beginDate 开始日期
 * @param endDate 截止日期
 */
export const getDateRange = (beginDate, endDate) => {
  return [dayjs(beginDate).startOf("d").format("YYYY-MM-DD HH:mm:ss"), dayjs(endDate).endOf("d").format("YYYY-MM-DD HH:mm:ss")];
};

/**
 * 将时间转换为 `几秒前`、`几分钟前`、`几小时前`、`几天前`
 * @param param 当前时间，new Date() 格式或者字符串时间格式
 * @param format 需要转换的时间格式字符串
 * @description param 10秒：  10 * 1000
 * @description param 1分：   60 * 1000
 * @description param 1小时： 60 * 60 * 1000
 * @description param 24小时：60 * 60 * 24 * 1000
 * @description param 3天：   60 * 60* 24 * 1000 * 3
 * @returns 返回拼接后的时间字符串
 */
export const formatPast = (timestamp, baseDate = new Date()) => {
  // 统一转换为Date对象
  const date = new Date(timestamp);
  const now = new Date(baseDate);

  // 计算时间差（毫秒）
  const diffInMs = now - date;
  const diffInSec = Math.floor(diffInMs / 1000);
  const diffInMin = Math.floor(diffInSec / 60);
  const diffInHour = Math.floor(diffInMin / 60);

  // 分级判断
  if (diffInSec < 60) {
    return `${diffInSec}秒前`;
  } else if (diffInMin < 60) {
    return `${diffInMin}分钟前`;
  } else if (diffInHour < 24) {
    return `${diffInHour}小时前`;
  } else {
    return formatDate(timestamp);
  }
};

/**
 * 文件大小单位转换
 * @param {number} bytes - 字节数（最小单位为 bit 时需先转成 bits）
 * @param {object} [options] - 配置选项
 * @param {boolean} [options.isBits=false] - 输入是否为比特（bits）
 * @param {number} [options.decimal=2] - 保留小数位数
 * @param {boolean} [options.trailingZeros=false] - 是否保留末尾的零
 * @returns {string} 格式化后的文件大小字符串
 */
export const formatFileSize = (bytes, { isBits = false, decimal = 2, trailingZeros = false } = {}) => {
  if (typeof bytes !== "number" || bytes < 0) return "0 B";

  // 转换单位阈值（按二进制 1024 进位）
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  // const base = isBits ? 8 : 1; // bits → bytes 转换率
  bytes = isBits ? bytes / 8 : bytes; // 统一转为 bytes 计算

  // 处理边界值
  if (bytes === 0) return `0 ${isBits ? "b" : units[0]}`;

  // 计算单位索引
  const unitIndex = Math.floor(Math.log(bytes) / Math.log(1024));
  const maxIndex = units.length - 1;
  const index = Math.min(unitIndex, maxIndex);

  // 计算结果
  const size = bytes / Math.pow(1024, index);
  const unit = index === 0 && isBits ? "b" : units[index];

  // 处理小数
  const fixedSize = size.toFixed(decimal);
  const formattedSize = trailingZeros ? fixedSize : fixedSize.replace(/\.?0+$/, ""); // 去除末尾零

  return `${formattedSize} ${unit}`;
};
